import { cn } from '@workspace/ui/lib/utils';
import { Snowflake, Wifi } from 'lucide-react';

interface CardVisualProps {
  cardName: string;
  lastFour?: string;
  type: 'VIRTUAL' | 'PHYSICAL';
  provider: string;
  status: string;
}

export function CardVisual({
  cardName,
  lastFour,
  type,
  provider,
  status,
}: CardVisualProps) {
  const isFrozen = status === 'frozen' || status === 'suspended';
  const isDisabled = status === 'disabled' || status === 'inactive';

  return (
    <div className="relative mx-auto w-full max-w-[370px]">
      <div
        className={cn(
          'relative aspect-[1.586/1] w-full rounded-2xl p-6 flex flex-col justify-between overflow-hidden transition-all duration-300',
          'bg-gradient-to-br shadow-2xl',
          type === 'VIRTUAL'
            ? 'from-violet-950 via-purple-900 to-indigo-950'
            : 'from-zinc-700 via-zinc-600 to-zinc-800',
          isFrozen && 'from-sky-950 via-blue-900 to-cyan-950',
          isDisabled && 'grayscale opacity-60',
        )}
      >
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(255,255,255,0.08),transparent_60%)]" />
        <div className="pointer-events-none absolute -right-8 -top-8 h-40 w-40 rounded-full bg-white/[0.03] blur-2xl" />

        {isFrozen && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-sky-500/10 backdrop-blur-[1px]">
            <Snowflake className="h-12 w-12 text-sky-300/60" strokeWidth={1} />
          </div>
        )}

        <div className="relative z-20 flex items-start justify-between">
          <div className="flex flex-col gap-0.5">
            <span className="text-[11px] font-medium uppercase tracking-wider text-white/50">
              {type === 'VIRTUAL' ? 'Virtual' : 'Physical'}
            </span>
            <span className="text-sm font-semibold text-white/90 truncate max-w-[200px]">
              {cardName}
            </span>
          </div>
          <Wifi className="h-5 w-5 rotate-90 text-white/40" />
        </div>

        <div className="relative z-20 flex flex-col gap-3">
          <div className="flex items-center gap-2">
            <span className="font-mono text-[17px] tracking-[0.18em] text-white/80">
              ····
            </span>
            <span className="font-mono text-[17px] tracking-[0.18em] text-white/80">
              ····
            </span>
            <span className="font-mono text-[17px] tracking-[0.18em] text-white/80">
              ····
            </span>
            <span className="font-mono text-[17px] tracking-[0.18em] text-white">
              {lastFour || '••••'}
            </span>
          </div>

          <div className="flex items-end justify-between">
            <div className="flex items-center gap-1.5">
              {isFrozen && (
                <span className="rounded-full bg-sky-400/20 px-2 py-0.5 text-[10px] font-medium text-sky-300">
                  FROZEN
                </span>
              )}
              {isDisabled && (
                <span className="rounded-full bg-red-400/20 px-2 py-0.5 text-[10px] font-medium text-red-300">
                  DISABLED
                </span>
              )}
            </div>
            <span className="text-lg font-bold tracking-wider text-white/70">
              {provider}
            </span>
          </div>
        </div>
      </div>

      <div
        className={cn(
          'absolute -inset-1 -z-10 rounded-2xl opacity-30 blur-xl transition-all',
          type === 'VIRTUAL'
            ? 'bg-gradient-to-br from-violet-600 to-indigo-600'
            : 'bg-gradient-to-br from-zinc-500 to-zinc-600',
          isFrozen && 'bg-gradient-to-br from-sky-500 to-cyan-500',
          isDisabled && 'opacity-10',
        )}
      />
    </div>
  );
}
